import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useAgentContext } from 'src/context/AgentContext'
import { translateSingleString } from 'src/localization/Trns'
import { usePostInsightsPrompt } from 'src/queries/agent/domain/insights.queries'
import {
  ConversationState,
  InterruptOption,
  PostAgentPromptRequest,
  PromptTemplate,
  StructuredJson,
} from 'src/queries/agent/network/agent.models'
import { assertNever } from 'src/queries/agent/network/agent.network'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'

export const useInsightsChat = (chatKey: string = 'insights') => {
  const locale = useSelector(selectNullSafeLocaleFromState)
  const lastErrorRef = useRef<Error | null>(null)

  const {
    agentShortcut,
    getMessages,
    addMessage,
    updateLastMessage,
    getConversationState,
    setConversationState,
    getThreadId,
    setThreadId,
  } = useAgentContext()

  const messages = getMessages(chatKey)
  const conversationState = getConversationState(chatKey)
  const threadId = getThreadId(chatKey)

  const {
    mutate: postPromptAgent,
    isPending,
    error,
  } = usePostInsightsPrompt({
    onSuccess: (data) => {
      setThreadId(chatKey, data.threadId)

      // update conversation state
      setConversationState(chatKey, data.currentState)

      let content: string
      let options: InterruptOption[] = []
      let suggestedPrompts: PromptTemplate[] | undefined
      let structuredJson: StructuredJson | undefined
      switch (data.currentState) {
        case ConversationState.MESSAGE:
          content = data.response
          suggestedPrompts = data.suggestedPrompts
          structuredJson = data.structuredJson
          break
        case ConversationState.INTERRUPT:
          content =
            (data.confirmationDetails
              ? data.confirmationDetails + '\n\n\n'
              : '') + data.question
          options = data.options
          break
        default:
          assertNever(
            data,
            'useInsightsChat.onSuccess - Invalid conversation state',
          )
      }

      // Check if last message is from AI and update it, or add new message
      const currentMessages = getMessages(chatKey)
      const lastMessage = currentMessages[currentMessages.length - 1]

      // Only update if the last AI message doesn't already have this exact content
      if (
        lastMessage &&
        lastMessage.sender === 'ai' &&
        lastMessage.content !== content
      ) {
        updateLastMessage(chatKey, {
          content,
          options,
          suggestedPrompts,
          structuredJson,
        })
      } else if (
        !lastMessage ||
        lastMessage.sender !== 'ai' ||
        lastMessage.content !== content
      ) {
        const type = data.currentState
        addMessage(chatKey, {
          sender: 'ai',
          type,
          options,
          suggestedPrompts,
          structuredJson,
          content,
          timestamp: new Date().toLocaleString(),
        })
      }
    },
  })

  // handle initial prompt
  useEffect(() => {
    if (!agentShortcut) return

    if ('initialPrompt' in agentShortcut) {
      startNewMessage({
        currentState: ConversationState.MESSAGE,
        prompt: agentShortcut.initialPrompt,
      })
    } else {
      startNewMessage({
        currentState: ConversationState.MESSAGE,
        promptTemplate: agentShortcut.initialPromptTemplate,
      })
    }
  }, [agentShortcut])

  function startNewMessage(input: PostAgentPromptRequest): void {
    postPromptAgent({ ...input, threadId })
    const timestamp = new Date().toLocaleString()

    switch (input.currentState) {
      case ConversationState.MESSAGE:
        if ('promptTemplate' in input) {
          addMessage(chatKey, {
            sender: 'user',
            type: ConversationState.MESSAGE,
            contentSrc:
              'displayPromptSrc' in input.promptTemplate
                ? input.promptTemplate.displayPromptSrc
                : undefined,
            content:
              'displayPrompt' in input.promptTemplate
                ? input.promptTemplate.displayPrompt
                : undefined,
            timestamp,
          })
        } else {
          addMessage(chatKey, {
            sender: 'user',
            type: ConversationState.MESSAGE,
            content: input.prompt,
            timestamp,
          })
        }
        break
      case ConversationState.INTERRUPT:
        addMessage(chatKey, {
          sender: 'user',
          type: ConversationState.INTERRUPT,
          content: input.feedback,
          timestamp,
        })
        break
      default:
        assertNever(
          input,
          'useInsightsChat.startNewMessage - Invalid conversation state',
        )
    }
  }

  useEffect(() => {
    // Only show error message if it's a new error we haven't seen before
    if (error && error !== lastErrorRef.current) {
      lastErrorRef.current = error
      // Error is handled by displaying a user-friendly message
      const errorMessage = translateSingleString(
        locale,
        'Sorry, something went wrong. Please try again later.',
      )
      addMessage(chatKey, {
        sender: 'ai',
        type: ConversationState.MESSAGE,
        content: errorMessage,
        timestamp: new Date().toLocaleString(),
      })
    } else if (!error) {
      // Reset the ref when error is cleared
      lastErrorRef.current = null
    }
  }, [error, chatKey, locale, addMessage])

  return { conversationState, isPending, messages, startNewMessage }
}
